'use client';

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

type NotificationType = 'success' | 'error' | 'warning';

interface NotificationState {
  message: string;
  type: NotificationType;
  id: number;
}

const NotificationContext = createContext<{
  showNotification: (message: string, type?: NotificationType) => void;
} | undefined>(undefined);

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notification, setNotification] = useState<NotificationState | null>(null);

  const showNotification = useCallback((message: string, type: NotificationType = 'success') => {
    const newNotification = { message, type, id: Date.now() };
    setNotification(newNotification);
    setTimeout(() => {
      setNotification(current => (current?.id === newNotification.id ? null : current));
    }, 4000); // Hide after 4 seconds
  }, []);

  const icons: Record<NotificationType, ReactNode> = {
    success: <CheckCircle className="h-6 w-6 text-green-500" />,
    error: <XCircle className="h-6 w-6 text-red-500" />,
    warning: <AlertTriangle className="h-6 w-6 text-yellow-500" />,
  };
  
  const borderColors: Record<NotificationType, string> = {
    success: 'border-green-500',
    error: 'border-red-500',
    warning: 'border-yellow-500',
  }

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      {notification && (
        <div 
          className={`fixed top-5 right-20 z-[100] w-full max-w-sm rounded-lg border-l-4 bg-white/80 dark:bg-gray-800/80 p-4 text-gray-800 dark:text-gray-100 shadow-2xl backdrop-blur-md transition-all duration-300 ease-in-out animate-slide-in ${borderColors[notification.type]}`}
          role="alert"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              {icons[notification.type]}
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{notification.message}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                type="button"
                className="-mx-1.5 -my-1.5 rounded-lg bg-white p-1.5 text-gray-400 hover:text-gray-900 focus:ring-2 focus:ring-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:text-white dark:focus:ring-gray-600"
                onClick={() => setNotification(null)}
                aria-label="Dismiss"
              >
                <span className="sr-only">Dismiss</span>
                <XCircle className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
